
import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import CredentialsForm from '@/components/CredentialsForm.vue'
import Input from '@/components/Input.vue'
import BaseButton from '@/components/BaseButton.vue'
import { LoginData } from '@/pages/login/entities/login'
import { loginUser } from '@/api/login'
import GrecaptchaBox from '@/components/GrecaptchaBox.vue'
import Checkbox from '@/components/Rain/Inputs/Checkbox.vue'
import MFASetup from '@/components/Settings/MFASetup.vue'
import MFASetupOk from '@/pages/login/MFASetupOk.vue'
import MFAVerifyRecoveryCode from '@/pages/login/MFAVerifyRecoveryCode.vue'

type Input = Vue & { hasError: boolean; hasFocused: boolean }
const DISABLED_SUBDOMAINS: string[] = ['asknicely']

@Component<Login>({
    components: {
        MFAVerifyRecoveryCode,
        MFASetupOk,
        MFASetup,
        Checkbox,
        GrecaptchaBox,
        BaseButton,
        Input,
        CredentialsForm,
    },
})
export default class Login extends Vue {
    public email = ''
    public password = ''
    public mfa = ''
    public mfaRememberDevice = false
    public loginData?: LoginData
    public error = ''
    public mfaLogin = false
    public mfaEnforce = false
    public mfaQrCode = ''
    public mfaSecret = ''
    public mfaSuccess = false
    public isMfaRecoveryPage = false
    public recoveryCodes: string[] = []
    public recoveryCode = ''
    public isLoading = true
    public redirectUrl = ''

    public async beforeMount() {
        // todo: add a check to see if the user is logged in via API/cookie/session
        const dataEl = document.getElementById('login-data')
        const dataJson = dataEl && dataEl.getAttribute('data-json')
        const data: LoginData = dataJson && JSON.parse(dataJson)

        this.loginData = data
        this.error = data.error
    }

    public async login() {
        this.error = ''
        let recaptchaResponse = ''

        if (this.loginData !== undefined && this.loginData.hasRecaptcha) {
            const grecaptcha = this.$refs.grecaptcha as GrecaptchaBox
            recaptchaResponse = grecaptcha.getResponse()
            if (recaptchaResponse.length < 1) {
                this.error = 'Please complete reCAPTCHA challenge'
                return
            }
        }

        if (!this.mfaLogin && !this.mfaEnforce) {
            (this.$refs.email as Input).hasFocused = true
            ;(this.$refs.password as Input).hasFocused = true
        }

        if (this.password === '' || !this.email.match(/.+@.+\..+/)) {
            return
        }

        // Don't allow empty mfa code submission
        if (this.mfaLogin && this.mfa === '') {
            return
        }

        try {
            const { data } = await loginUser(
                this.email,
                this.password,
                this.mfaLogin || this.mfaEnforce ? this.mfa : undefined,
                this.loginData !== undefined && this.loginData.hasRecaptcha
                    ? recaptchaResponse
                    : undefined,
                this.mfaLogin ? this.mfaRememberDevice : undefined,
                this.recoveryCode
            )
            if (data.mfa) {
                this.mfaLogin = true
                this.$nextTick(() => {
                    (this.$refs.mfa as HTMLInputElement).focus()
                })
            } else if (data.mfaEnforce) {
                this.mfaEnforce = true
                this.mfaQrCode = data.codeUrl
                this.mfaSecret = data.mfaSecret
            } else if (data.recoveryCodes) {
                this.recoveryCodes = data.recoveryCodes
                this.mfaSuccess = true
                this.isLoading = false
                this.redirectUrl = data.redirect
            } else {
                this.redirectTo(data.redirect)
            }
        } catch (e: any) {
            this.error = e.response.data.error
        }
    }

    public async verifyRecoveryCode() {
        try {
            this.error = ''
            let recaptchaResponse = ''

            const { data } = await loginUser(
                this.email,
                this.password,
                this.mfaLogin || this.mfaEnforce ? this.mfa : undefined,
                this.loginData !== undefined && this.loginData.hasRecaptcha
                    ? recaptchaResponse
                    : undefined,
                this.mfaLogin ? this.mfaRememberDevice : undefined,
                this.recoveryCode
            )

            if (data.recoveryCodes) {
                this.recoveryCodes = data.recoveryCodes
                this.mfaSuccess = true
                this.isLoading = false
                this.redirectUrl = data.redirect
            } else {
                this.redirectTo(data.redirect)
            }
        } catch (e: any) {
            this.error = e.response.data.error
        }
    }

    public mfaValidateLogin(mfaCode: string) {
        if (!mfaCode || mfaCode.length !== 6) {
            this.error = 'Please enter your 6 digit MFA code'
            return
        }
        this.mfa = mfaCode
        this.login()
    }

    public onDownloadRecoveryCodes() {
        const dataAsString = this.recoveryCodes.join('\n')
        const blob = new Blob([dataAsString], { type: 'text/plain' })
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = 'recovery-codes.txt'
        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    public created() {
        window.addEventListener('keydown', (e) => {
            const isCommandG = (e.metaKey || e.ctrlKey) && e.key === 'g'
            const subDomain = window.location.host.split('.')[0]
            const notDisabled = DISABLED_SUBDOMAINS.indexOf(subDomain) === -1
            if (
                notDisabled &&
                isCommandG &&
                this.loginData !== undefined &&
                this.loginData.enabledGLogin
            ) {
                e.preventDefault()
                this.redirectTo(this.loginData.asknicelyLoginUrl)
            }
        })
    }

    public mfaCancel() {
        this.error = ''
        this.mfaEnforce = false
        this.mfaQrCode = ''
        this.mfaSecret = ''
        this.isMfaRecoveryPage = false
        this.recoveryCode = ''
    }

    public redirectTo(link: string) {
        return location.replace(link)
    }

    public onChangeRememberDevice(value: boolean): void {
        this.mfaRememberDevice = value
    }

    public onLostAuthenticatorLink(): void {
        this.isMfaRecoveryPage = true
    }

    public onVerifyRecoveryCode(recoveryCode: string) {
        if (!recoveryCode) {
            this.error = 'Recovery code required.'
            return
        }
        this.recoveryCode = recoveryCode
        this.verifyRecoveryCode()
    }

    public onDone() {
        this.redirectTo(this.redirectUrl)
    }
}
